<template>
  <v-dialog
    v-model="showDialogInvoicePayment"
    width="400"
    ref="container"
    scrollable
    :fullscreen="$mq === 'xs'"
    transition="scroll-y-reverse-transition"
  >
    <v-card :tile="$mq === 'xs'">
      <v-card-title class="blue darken-2 white--text">
        <h2 class="text-h4 mb-1 mx-auto">
          <transition name="fade-fast" mode="out-in">
            <span v-if="selectedPaymentMethod" key="selected-method-header">
              <v-btn
                @click="switchPaymentFn"
                color="white"
                icon
                absolute
                left
                outlined
                :class="$mq | mq({ xs: 'mt-1', sm: '' })"
                :small="$mq === 'xs'"
              >
                <v-icon :small="$mq === 'xs'">mdi-arrow-left</v-icon></v-btn
              >
              {{ selectedPaymentMethod }}</span
            >
            <span v-else key="pay-and-reserve"
              >Pay &amp; Reserve
              <v-btn
                @click="showDialogInvoicePayment = false"
                color="white"
                :class="$mq | mq({ xs: 'mt-1', sm: '' })"
                icon
                absolute
                right
                top
                outlined
                :small="$mq === 'xs'"
              >
                <v-icon :small="$mq === 'xs'">mdi-close</v-icon></v-btn
              >
            </span>
          </transition>
        </h2>
      </v-card-title>

      <v-card-text :class="$mq | mq({ xs: 'pa-3 pt-0', sm: 'pt-0 px-6 pb-5' })">
        <div
          class="mb-3 py-4"
          v-if="
            paymentInfo &&
              !getIsAdvancedPaymentOn &&
              $route.path !== '/settings'
          "
        >
          <AirtableHtml tag-class="" setting-name="Payment Info" />
        </div>
        <div v-else class="payment-panels--wrap">
          <v-row
            :class="[
              'payment-panels',
              { 'payment-selected': showPaymentDetails },
            ]"
          >
            <v-col class="payment-options py-4" key="all-methods">
              <div class="mb-3" v-if="paymentInfo">
                <AirtableHtml tag-class="" setting-name="Payment Info" />
              </div>
              <div v-else class="airtable-html"
                >Please contact us at
                <a :href="`mailto:${this.getContactEmail}`">{{
                  this.getContactEmail
                }}</a>
                to complete payment and confirm your order.</div
              >
              <h2
                class="text-h5 text-center mb-2 font-weight-bold font-italic"
                v-if="getPaymentOptions.length"
                >Select Payment Method</h2
              >
              <template v-for="option of getPaymentOptions">
                <v-btn
                  depressed
                  block
                  class="mb-3 white--text"
                  :color="
                    `${
                      selectedPaymentMethod === option
                        ? 'purple accent-4'
                        : 'blue darken-2'
                    }`
                  "
                  @click="paymentSelectionFn(option)"
                  style="position:relative;"
                  :key="`${option}-btn`"
                  large
                  >{{ option === SQUARE ? 'Credit Card (Square)' : option
                  }}<v-icon right small style="position:absolute;right:0;"
                    >mdi-arrow-right</v-icon
                  ></v-btn
                >
              </template>
            </v-col>
            <v-col
              outlined
              class="method-details py-6"
              ref="method-details"
              key="method-details"
            >
              <v-card
                outlined
                class="order-number-module py-3 px-3 mb-4 text-center"
                v-if="showOrderNumberModule"
              >
                <v-icon color="yellow darken-2 mb-1 mx-auto" large
                  >mdi-alert-outline</v-icon
                >
                <h3
                  class="text-center font-weight-bold deep-orange--text text--darken-2 order-number-warning"
                >
                  Please include your order number in your payment message:</h3
                >
                <BtnCopiable
                  :copiable="`Order #${order.ticket_number}`"
                  :label="`Order #${order.ticket_number}`"
                  @copySuccess="copySuccess"
                  color="black"
                  large
                />
                <div class="text-body-2">Click to Copy</div>
              </v-card>
              <div class="text-h4 font-weight-bold black--text text-center mb-3"
                >Total: {{ order.grand_total_formatted
                }}<BtnCopiableIcon
                  v-if="
                    !_includes([CREDIT_CARD, SQUARE], selectedPaymentMethod)
                  "
                  :copiable="order.grand_total_usd"
                  class="ml-3 mb-1"
                  label="Copy Total"
                  @copySuccess="copySuccess"
                  color="grey darken-2"
              /></div>

              <!-- START PAYMENT DETAILS -->

              <!-- START CC -->
              <template v-if="selectedPaymentMethod === CREDIT_CARD">
                <AirtableHtml
                  v-if="getShopDetail(CREDIT_CARD, '').length"
                  :setting-name="CREDIT_CARD"
                  tag-class="ma-3"
                />
                <div class="text-center">
                  <v-btn
                    v-if="hasCreditPhone"
                    outlined
                    color="blue darken-2"
                    :href="`tel:+1${hasCreditPhone.replace(/\D/g, '')}`"
                    x-large
                    >{{ hasCreditPhone
                    }}<v-icon right small>mdi-phone</v-icon></v-btn
                  >
                </div>
              </template>
              <!-- / CC -->

              <!-- START CASH -->
              <template
                v-else-if="
                  selectedPaymentMethod === CASH && getPaymentOption(CASH)
                "
              >
                <div class="text-h6">{{ getPaymentOption(CASH) }}</div>
              </template>
              <!-- / CASH -->

              <!-- START VENMO -->
              <template v-else-if="selectedPaymentMethod === VENMO">
                <template v-if="getPaymentOption(VENMO)">
                  <v-btn
                    depressed
                    class="my-5 white--text mx-auto d-flex"
                    color="deep-orange darken-2"
                    :href="
                      isIOS ? venmoDeepLink : `https://venmo.com/${venmoName}`
                    "
                    target="_blank"
                    :large="!isIOS"
                    :x-large="isIOS"
                    >Pay in {{ VENMO }}</v-btn
                  >
                </template>
                <div class="text-h5 text-center">
                  Find us by name:
                </div>
                <BtnCopiable
                  v-if="venmoName"
                  :copiable="`@${venmoName}`"
                  :label="`@${venmoName}`"
                  @copySuccess="copySuccess"
                  large
                  text
                />
              </template>
              <!-- / VENMO -->

              <!-- START APPLE_PAY -->
              <template
                v-else-if="
                  selectedPaymentMethod === APPLE_PAY &&
                    getPaymentOption(APPLE_PAY)
                "
              >
                <div class="text-h6"
                  >Open Messages on your Apple device and compose a new message
                  to:</div
                >
                <BtnCopiable
                  :copiable="formattedAppleNumber"
                  :label="formattedAppleNumber"
                  @copySuccess="copySuccess"
                  :outlined="false"
                  color="deep-orange darken-2"
                  class="white--text"
                  depressed
                  x-large
                />
                <div class="text-center">
                  <small>Click to Copy</small>
                </div>
                <div class="text-h6"
                  >Click the black Pay button, set the amount to
                  {{ order.grand_total_formatted }} and tap Pay.</div
                >
                <v-btn
                  text
                  href="https://support.apple.com/en-us/HT207875"
                  target="_blank"
                  color="blue darken-3"
                  class="mt-4"
                  small
                  block
                  >Detailed Instructions<v-icon right x-small
                    >mdi-information-outline</v-icon
                  ></v-btn
                >
              </template>
              <!-- / APPLE_PAY -->

              <!-- START PAYPAL -->
              <template
                v-else-if="
                  selectedPaymentMethod === PAYPAL && getPaymentOption(PAYPAL)
                "
              >
                <v-btn
                  depressed
                  block
                  class="mb-3 white--text"
                  color="deep-orange darken-2"
                  target="_blank"
                  large
                  :href="paypalLink"
                  >Pay in {{ PAYPAL }}</v-btn
                >
                <div class="mt-8 text-h5 text-center">
                  Find Us by Email
                </div>
                <BtnCopiable
                  v-if="paypalEmail"
                  :copiable="`${paypalEmail}`"
                  :label="`${paypalEmail}`"
                  @copySuccess="copySuccess"
                  large
                />
                <v-btn
                  depressed
                  text
                  small
                  class="my-3 mx-auto d-flex"
                  color="blue darken-2"
                  target="_blank"
                  href="https://paypal.com"
                  >Visit PayPal.com</v-btn
                >
              </template>
              <!-- / PAYPAL -->

              <!-- START CASHAPP -->
              <template v-else-if="selectedPaymentMethod === CASHAPP">
                <v-btn
                  v-if="cashtag"
                  depressed
                  block
                  class="mb-3 white--text"
                  color="deep-orange darken-2"
                  target="_blank"
                  large
                  :href="`https://cash.app/$${cashtag.replace('$', '')}`"
                  >Pay in {{ CASHAPP }}</v-btn
                >
                <div class="text-h5 text-center">
                  Find us by name:
                </div>
                <BtnCopiable
                  v-if="cashtag"
                  :copiable="`${cashtag}`"
                  :label="`${cashtag}`"
                  @copySuccess="copySuccess"
                  large
                />
              </template>
              <!-- / CASHAPP -->

              <!-- START ZELLE -->
              <template
                v-else-if="
                  selectedPaymentMethod === ZELLE && getPaymentOption(ZELLE)
                "
              >
                <h2 class="text-h6"
                  >Using your preferred bank's website, their app, or the Zelle
                  app, send us a payment. You may need to add us as a new
                  recipient using:</h2
                >
                <div class="text-h5 black--text my-4 text-center">{{
                  getPaymentOption('Zelle')
                }}</div>
                <BtnCopiable
                  :copiable="getPaymentOption('Zelle')"
                  label="Copy Zelle Info"
                  @copySuccess="copySuccess"
                  block
                />
                <v-btn
                  text
                  href="https://www.zellepay.com/"
                  target="_blank"
                  color="blue darken-3"
                  class="mt-4"
                  small
                  block
                  >More about Zelle<v-icon right x-small
                    >mdi-information-outline</v-icon
                  ></v-btn
                >
              </template>
              <!-- / ZELLE -->

              <!-- START SQUARE -->
              <template v-else-if="selectedPaymentMethod === SQUARE">
                <BtnSquarePayment :order="order" />
              </template>
              <!-- / SQUARE -->

              <!-- END PAYMENT DETAILS -->
            </v-col>
          </v-row>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-btn
          @click="showDialogInvoicePayment = false"
          block
          depressed
          color="grey lighten-2"
          >Close</v-btn
        >
      </v-card-actions>
    </v-card>
    <v-snackbar v-model="showSnackbar" class="text-h6 clipboard-snackbar"
      >"{{ snackbarText }}"<br />copied to clipboard.</v-snackbar
    >
  </v-dialog>
</template>

<script>
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import { mapFields } from 'vuex-map-fields'
import { mapGetters, mapActions } from 'vuex'
import { isIOS } from 'mobile-device-detect'
import _includes from 'lodash/includes'
import AirtableHtml from '@/components/AirtableHtml'
import BtnCopiable from '@/components/BtnCopiable'
import BtnCopiableIcon from '@/components/BtnCopiableIcon'
import BtnSquarePayment from '@/components/BtnSquarePayment'

import {
  ShopPaymentOptions,
  PAYPAL,
  VENMO,
  CASHAPP,
  CREDIT_CARD,
  SQUARE,
  APPLE_PAY,
  CASH,
  ZELLE,
} from '@/constants/PaymentProviders'

export default {
  name: 'DialogInvoicePayment',
  props: {
    order: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      showSnackbar: false,
      snackbarText: '',
      selectedPaymentMethod: '',
      showPaymentDetails: false,
      isIOS,
      ShopPaymentOptions,
      PAYPAL,
      VENMO,
      CASHAPP,
      CREDIT_CARD,
      SQUARE,
      APPLE_PAY,
      CASH,
      ZELLE,
    }
  },
  components: { AirtableHtml, BtnCopiable, BtnCopiableIcon, BtnSquarePayment },
  computed: {
    ...mapGetters('shop', [
      'getShopDetail',
      'getBooleanToggle',
      'getIsAdvancedPaymentOn',
      'getPaymentOptions',
      'getContactEmail',
    ]),
    ...mapFields('order', ['showDialogInvoicePayment']),

    paymentInfo() {
      let shopInfo = this.getShopDetail('Payment Info')
      var empty = new RegExp('<p>\\s*<\\/p>', 'i')
      if (empty.test(shopInfo)) {
        shopInfo = ''
      }
      return shopInfo
    }, // paymentInfo

    venmoName() {
      return this.getPaymentOption(VENMO, '').replace('@', '')
    }, // venmoName

    venmoDeepLink() {
      return `venmo://paycharge?txn=pay&recipients=${this.venmoName}&amount=${this.order.grand_total_usd}&note=${this.venmoNote}`
    }, // venmoDeepLink

    venmoNote() {
      return encodeURIComponent(`Order #${this.order.ticket_number}`)
    }, // venmoNote

    cashtag() {
      return this.getPaymentOption(CASHAPP)
    }, // cashtag

    paypalEmail() {
      return this.getPaymentOption(PAYPAL)
    }, // paypalEmail

    paypalLink() {
      return (
        `https://www.paypal.com/cgi-bin/webscr?cmd=_xclick&business=${this.getPaymentOption(
          PAYPAL
        )}&tax=0&currency=USD&item_name=` +
        encodeURIComponent(`Minimart Order #${this.order.ticket_number}`) +
        `&item_number=${this.order.id}&quantity=1&amount=${this.order.grand_total_usd}`
      )
    }, // paypalLink

    hasCreditPhone() {
      return this.getShopDetail('Credit Card Phone')
    }, // hasCreditPhone

    showOrderNumberModule() {
      const notCashOrCredit = !_includes(
        [CASH, CREDIT_CARD, SQUARE],
        this.selectedPaymentMethod
      )
      const isVenmoOnIOS = this.selectedPaymentMethod === VENMO && this.isIOS
      return notCashOrCredit && !isVenmoOnIOS
    }, // showOrderNumberModule

    formattedAppleNumber() {
      if (!this.getPaymentOption(APPLE_PAY)) return false
      const parsed = parsePhoneNumberFromString(
        this.getPaymentOption(APPLE_PAY),
        'US'
      )
      return parsed.formatNational()
    }, // formattedAppleNumber
  }, // computed
  methods: {
    ...mapActions('order', ['setDialogInvoicePayment']),

    copySuccess(e) {
      this.snackbarText = e.text
      this.showSnackbar = true
    }, // copySuccess
    onError: function() {
      alert('Failed to copy the text to the clipboard')
    },
    reset() {
      this.showSnackbar = false
      this.selectedPaymentMethod = false
      this.showPaymentDetails = false
      this.snackbarText = ''
    }, // reset
    switchPaymentFn() {
      this.showPaymentDetails = false
      setTimeout(() => (this.selectedPaymentMethod = false), 300)
    }, // switchPaymentFn
    paymentSelectionFn(option) {
      this.selectedPaymentMethod = option
      this.showPaymentDetails = true
      setTimeout(() => {
        this.$refs['method-details'].scrollIntoView({ behavior: 'smooth' })
      }, 400)
    }, // paymentSelectionFn

    getPaymentOption(provider, defaultVal = undefined) {
      return this.getShopDetail(ShopPaymentOptions[provider], defaultVal)
    },
    _includes,
  }, // methods
}
</script>

<style lang="scss">
.payment-panels--wrap {
  position: relative;
  overflow: hidden;
}
.payment-panels {
  transition: transform 0.4s ease-out;
  width: calc(200% + 48px);
  left: 0;
  transform: translateX(0%);
  &.payment-selected {
    transform: translateX(-50%);
  }
}

.order-number-warning {
  font-size: 1.25rem;
  line-height: 1.2;
}

.clipboard-snackbar {
  max-width: calc(100% - (8px * 2));
}
</style>
